import React from 'react';
import styled from 'styled-components';

import ArrowLeftWhiteSvg from '../media/arrow_left_white.svg';
import TagBetaSvg from '../media/tags/beta.svg';
import TagPatentPendingSvg from '../media/tags/patent_pending_white.svg';
import TagPrototype from '../media/tags/prototype.svg';

import BlueFireworkPanel from './blueFireworkPanel';
import {pxToEm} from './globalStyles';

//#region Styled Components

const StyledContentDiv = styled.div`
  position: relative;
  height: ${pxToEm(224)}em;
  text-align: center;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StyledBackButton = styled.button`
  position: absolute;
  left: 15%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;

  display: flex;
  justify-content: center;
  align-items: center;

  background: rgba(255, 255, 255, 0.3);
  font-weight: bold;
  line-height: 1em;
  color: white;
  padding: 0.7em 1.2em;
  border: 2px solid transparent;
  border-radius: 1.45em;
  cursor: pointer;

  :hover {
    background: rgba(255, 255, 255, 0.5);
  }

  :focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.7);
  }

  > img {
    height: 1.5em;
    margin-right: 0.5em;
  }
`;

const topSubHeadlineFontSizePx = 18;
const StyledTopSubHeadline = styled.p`
  font-size: ${pxToEm(topSubHeadlineFontSizePx)}em;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.7);
  margin-top: ${pxToEm(33) / pxToEm(topSubHeadlineFontSizePx)}em;
  margin-bottom: 0;
`;

const headlineFontSizePx = 27;
const StyledHeadline = styled.h1`
  font-size: ${pxToEm(headlineFontSizePx)}em;
  color: white;
  margin-top: ${pxToEm(39) / pxToEm(headlineFontSizePx)}em;
  margin-bottom: 0;
`;

const StyledLeftBadgeContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 15%;
  transform: translateY(50%);
`;

const StyledCenterBadgeContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(50%);
`;

const StyledRightBadgeContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 15%;
  transform: translateY(50%);
`;

const StyledBgWrapperDiv = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

const StyledBlueFireworkPanel = styled(BlueFireworkPanel)`
  width: 100%;
  height: 100%;
`;

const StyledOrangeGradient = styled.div`
  width: 100%;
  height: 4px;
  background: linear-gradient(269.52deg, #ffffff 50%, #ff6b00 100%);
  position: absolute;
  bottom: 0;
  transform: translateY(0%);
`;

const StyledBetaTagImg = styled.img`
  position: absolute;
  bottom: 0;
  left: 55%;
  transform: translateY(50%);
`;

const StyledPatentPendingTagImg = styled.img`
  position: absolute;
  right: 15%;
  top: 50%;
  transform: translateY(-50%);
`;

//#endregion

interface Props {
  headline: string;
  topSubHeadline: string;
  backButton?: boolean;
  onBackButtonClick?: () => void;
  badgeComponent?: React.ReactNode;
  leftBadgeComponent?: React.ReactNode;
  rightBadgeComponent?: React.ReactNode;
  hasBetaTag?: boolean;
  hasPatentPendingTag?: boolean;
  hasPrototypeTag?: boolean;
}

const PageViewHeadline: React.FunctionComponent<Props> = ({
  headline,
  topSubHeadline,
  backButton = false,
  onBackButtonClick = () => undefined,
  badgeComponent,
  leftBadgeComponent,
  rightBadgeComponent,
  hasBetaTag = false,
  hasPatentPendingTag = false,
  hasPrototypeTag = false
}) => {
  const backButtonComponent = (
    <StyledBackButton onClick={onBackButtonClick}>
      <img src={ArrowLeftWhiteSvg} alt="ArrowLeftWhiteSvg" />
      <span>Back</span>
    </StyledBackButton>
  );

  return (
    <StyledContentDiv>
      <StyledBgWrapperDiv>
        <StyledBlueFireworkPanel
          fireworks={[{width: '43%', left: '-15%', top: '-80%'}, {width: '43%', right: '-15%', top: '-80%'}]}
        />
        <StyledOrangeGradient />
      </StyledBgWrapperDiv>

      <StyledContent>
        <StyledTopSubHeadline>{topSubHeadline}</StyledTopSubHeadline>
        <StyledHeadline>{headline}</StyledHeadline>
      </StyledContent>

      {backButton && backButtonComponent}

      {leftBadgeComponent && <StyledLeftBadgeContainer>{leftBadgeComponent}</StyledLeftBadgeContainer>}
      {badgeComponent && <StyledCenterBadgeContainer>{badgeComponent}</StyledCenterBadgeContainer>}
      {rightBadgeComponent && <StyledRightBadgeContainer>{rightBadgeComponent}</StyledRightBadgeContainer>}

      {hasBetaTag && <StyledBetaTagImg src={TagBetaSvg} alt="TagBetaSvg" />}
      {hasPatentPendingTag && <StyledPatentPendingTagImg src={TagPatentPendingSvg} alt="TagPatentPendingSvg" />}
      {hasPrototypeTag && <StyledBetaTagImg src={TagPrototype} alt="TagPrototypeSvg" />}
    </StyledContentDiv>
  );
};

export default PageViewHeadline;
